import { supabase } from '../../supabase';
import { authedFetch } from '../../utils/authedRequest';
import { getColourAnalysisRequestUrl } from '../../config';

const BUCKET = 'colour-analysis';

function photoBlob(dataUrl) {
  const match = /^data:(image\/(?:jpeg|png|webp));base64,(.+)$/.exec(dataUrl);
  if (!match) throw new Error('Prepared photo has an unsupported format.');
  const bytes = Uint8Array.from(atob(match[2]), (character) => character.charCodeAt(0));
  return { blob: new Blob([bytes], { type: match[1] }), type: match[1] };
}

async function registeredUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user || data.user.is_anonymous) throw new Error('Sign in to save your colours.');
  return data.user;
}

export async function saveColourReport({ runId, analysis, colourExperience, photoData }) {
  const user = await registeredUser();
  if (!Array.isArray(photoData) || photoData.length < 1 || photoData.length > 3) {
    throw new Error('Choose one to three photos to save this result.');
  }
  const uploaded = [];
  try {
    for (let index = 0; index < photoData.length; index += 1) {
      const { blob, type } = photoBlob(photoData[index]);
      const extension = type === 'image/png' ? 'png' : type === 'image/webp' ? 'webp' : 'jpg';
      const path = `${user.id}/${runId}/${index + 1}.${extension}`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { contentType: type, upsert: false });
      if (error) throw error;
      uploaded.push(path);
    }
    const { data, error } = await supabase.from('colour_analysis_reports').insert({
      id: runId, user_id: user.id, analysis, colour_experience: colourExperience,
      photo_paths: uploaded,
    }).select('id, created_at').single();
    if (error) throw error;
    return data;
  } catch (error) {
    if (uploaded.length) await supabase.storage.from(BUCKET).remove(uploaded);
    throw error;
  }
}

export async function latestColourReport() {
  const user = await registeredUser();
  const { data, error } = await supabase.from('colour_analysis_reports')
    .select('id, analysis, colour_experience, photo_paths, created_at')
    .eq('user_id', user.id).order('created_at', { ascending: false }).limit(1).maybeSingle();
  if (error) throw error;
  return data;
}

export async function listColourReports() {
  const user = await registeredUser();
  const { data, error } = await supabase.from('colour_analysis_reports')
    .select('id, analysis, colour_experience, photo_paths, created_at')
    .eq('user_id', user.id).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function signedColourReportPhotos(report) {
  const user = await registeredUser();
  const paths = Array.isArray(report.photo_paths) ? report.photo_paths : [];
  if (paths.some((path) => typeof path !== 'string' || !path.startsWith(`${user.id}/`))) {
    throw new Error('The photo owner does not match this account.');
  }
  return Promise.all(paths.map(async (path) => {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
    return error ? null : data?.signedUrl || null;
  }));
}

export async function updateColourReportExperience(reportId, colourExperience) {
  const user = await registeredUser();
  if (!reportId || !user.id) throw new Error('Open a saved report to update your colours.');
  const response = await authedFetch(getColourAnalysisRequestUrl(), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ colourAnalysisV1: 'revise', report_id: reportId,
      colour_experience: colourExperience }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error('Your changes could not be saved. Please try again.');
  return payload.colour_experience;
}

export async function deleteColourReport(report) {
  const user = await registeredUser();
  const paths = report.photo_paths || [];
  if (paths.some((path) => !path.startsWith(`${user.id}/`))) throw new Error('The photo owner does not match this account.');
  if (paths.length) {
    const { error } = await supabase.storage.from(BUCKET).remove(paths);
    if (error) throw error;
  }
  const { error } = await supabase.from('colour_analysis_reports').delete()
    .eq('id', report.id).eq('user_id', user.id);
  if (error) throw error;
}
